import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { createDefaultWebhookBroadcastFormData, webhookBroadcastModalStore } from "../stores/webhookBroadcastModal";
import { webhookPresetsStore } from "../stores/webhookPresets";
import CohortConditionsEditor from "./CohortConditionsEditor";

function WebhookBroadcastModal({ Interface }) {
	const modal = useRecoilValue(webhookBroadcastModalStore);
	const setModal = useSetRecoilState(webhookBroadcastModalStore);
	const presets = useRecoilValue(webhookPresetsStore);
	const formData = modal.formData;

	React.useEffect(() => {
		if (modal.show && !presets.loaded) {
			Interface.loadWebhookPresets();
		}
	}, [modal.show, presets.loaded, Interface]);

	const updateField = (field, value) => {
		setModal({
			...modal,
			formData: {
				...formData,
				[field]: value,
			},
		});
	};

	const closeModal = () => {
		setModal({
			...modal,
			show: false,
			loaded: true,
			formData: createDefaultWebhookBroadcastFormData(),
			audiencePreview: {
				loading: false,
				count: null,
				error: "",
			},
		});
	};

	const previewAudience = async (cohortConditions) => {
		setModal({
			...modal,
			formData: {
				...formData,
				cohortConditions,
			},
			audiencePreview: {
				loading: true,
				count: null,
				error: "",
			},
		});

		try {
			const response = await Interface.request("get", { class: "data", module: "webhookBroadcast", method: "previewAudience" }, { cohortConditions });
			setModal({
				...modal,
				formData: {
					...formData,
					cohortConditions,
				},
				audiencePreview: {
					loading: false,
					count: Number(response?.count || 0),
					error: "",
				},
			});
		} catch (error) {
			setModal({
				...modal,
				formData: {
					...formData,
					cohortConditions,
				},
				audiencePreview: {
					loading: false,
					count: null,
					error: error?.text || error?.toString?.() || "Failed to preview audience",
				},
			});
		}
	};

	const createBroadcast = async () => {
		if (!formData.name.trim()) {
			Interface.addNoty("Error", "Broadcast name is required");
			return;
		}

		if (!formData.presetId) {
			Interface.addNoty("Error", "Preset is required");
			return;
		}

		const presetIdAsNumber = Number(formData.presetId);
		const payload = {
			name: formData.name.trim(),
			presetId: Number.isNaN(presetIdAsNumber) ? formData.presetId : presetIdAsNumber,
			cohortConditions: formData.cohortConditions || [],
		};

		setModal({ ...modal, loaded: false });

		try {
			await Interface.request("set", { class: "data", module: "webhookBroadcast", method: "create" }, payload);
			Interface.addNoty("Success", "Webhook broadcast queued");
			closeModal();
			Interface.loadWebhookBroadcasts();
		} catch (error) {
			Interface.addNoty("Error", error?.text || error?.toString?.() || "Request failed");
			setModal({ ...modal, loaded: true });
		}
	};

	const presetOptions = presets.data
		.filter((preset) => preset.active === true || preset.active === 1 || preset.active === "1")
		.map((preset) => ({
			value: String(preset.id),
			label: `${preset.name || "Preset"} (#${preset.id})`,
		}));
	const selectedPreset = presetOptions.find((option) => option.value === String(formData.presetId)) || null;

	return (
		<Modal
			visible={modal.show}
			onDismiss={closeModal}
			header="Create webhook broadcast"
			size="max"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="normal" onClick={closeModal}>
							Close
						</Button>
						<Button variant="primary" loading={!modal.loaded} onClick={createBroadcast}>
							Create and queue
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			<SpaceBetween size="m">
				{!presetOptions.length && presets.loaded ? <Alert type="warning">Create an active webhook preset before creating a broadcast.</Alert> : null}
				<FormField label="Broadcast name" stretch>
					<Input value={formData.name} placeholder="June CRM export" onChange={({ detail }) => updateField("name", detail.value)} />
				</FormField>
				<FormField label="Preset" stretch>
					<Select
						loadingText="Loading presets"
						placeholder="Select webhook preset"
						disabled={!presets.loaded}
						selectedOption={selectedPreset}
						options={presetOptions}
						onChange={({ detail }) => updateField("presetId", detail.selectedOption.value)}
					/>
				</FormField>
				<CohortConditionsEditor
					conditions={formData.cohortConditions}
					onChange={(cohortConditions) => updateField("cohortConditions", cohortConditions)}
					onPreview={previewAudience}
					previewCount={modal.audiencePreview.count}
					previewLoading={modal.audiencePreview.loading}
					previewError={modal.audiencePreview.error}
				/>
			</SpaceBetween>
		</Modal>
	);
}

export default WebhookBroadcastModal;
